import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchMediaPosts, type MediaPost } from '@/lib/media';

function CultureMediaSection() {
  const [posts, setPosts] = useState<MediaPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await fetchMediaPosts();
        if (!cancelled) {
          setPosts(data.slice(0, 6));
        }
      } catch {
        /* ignore silently on this decorative section */
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <section className="py-10 md:py-14 bg-background-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex items-center gap-3 text-foreground-400">
            <i className="ri-loader-4-line animate-spin text-xl" />
            <span className="text-sm">Chargement du contenu média…</span>
          </div>
        </div>
      </section>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <section className="py-12 md:py-16 bg-background-100 border-b border-background-200/70">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="inline-block text-xs font-semibold text-accent-400 uppercase tracking-wider mb-1">
              Média & Créateurs
            </span>
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-50">
              Les dernières créations de la communauté
            </h2>
          </div>
          <Link
            to="/media"
            className="flex items-center gap-1.5 text-sm font-medium text-accent-400 hover:gap-2.5 transition-all whitespace-nowrap"
          >
            <span>Tout voir</span>
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {posts.map((post) => (
            <Link
              key={post.id}
              to={`/media?cat=${encodeURIComponent(post.category)}`}
              className="group rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-accent-400/40 transition-all"
            >
              <div className="relative aspect-video w-full overflow-hidden bg-background-200/60">
                {post.thumbnail_url ? (
                  <img
                    src={post.thumbnail_url}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-video-line text-4xl text-foreground-400" />
                  </div>
                )}
                <span className="absolute top-2 left-2 px-2 py-1 rounded-md bg-foreground-950/70 text-foreground-50 text-xs font-medium">
                  {post.category}
                </span>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50/90">
                    <i className="ri-play-fill text-xl text-foreground-50" />
                  </div>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-medium text-sm text-foreground-50 mb-1 truncate">
                  {post.title}
                </h3>
                <div className="flex items-center gap-3 text-xs text-foreground-400">
                  {post.artist_name && (
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-user-star-line text-accent-400" />
                      {post.artist_name}
                    </span>
                  )}
                  {post.location && (
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-map-pin-line text-accent-400" />
                      {post.location}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-eye-line" />
                    {post.views} vues
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default CultureMediaSection;